/**
 * Butter Logo Component
 * 
 * SVG logo with proper React patterns and accessibility.
 * Supports customizable size and colors via Tailwind classes.
 * 
 * @example
 * ```tsx
 * // Default (full logo with text)
 * <ButterLogo />
 * 
 * // Icon only
 * <ButterLogo variant="icon" />
 * 
 * // Custom size
 * <ButterLogo className="h-12 w-auto" />
 * 
 * // Custom colors
 * <ButterLogo className="text-purple-600" />
 * ```
 */

import { ButterLogoFullLight } from './ButterLogoFullLight';

interface ButterLogoProps {
  /** Variant of the logo to display */
  variant?: 'full' | 'icon' | 'wordmark';
  /** Additional CSS classes */
  className?: string;
  /** Accessible title for screen readers */
  title?: string;
  /** Whether to hide from screen readers */
  ariaHidden?: boolean;
  /**
   * The ground the logo sits on — not a theme. `dark` (the default) is what
   * every light surface has always rendered; `light` is for a dark ground.
   *
   * For the `full` variant this swaps in design-supplied artwork
   * (`ButterLogoFullLight`) rather than recolouring, because the two lockups
   * have different proportions (155×56 vs 100×32). The mark stays purple in
   * both; only the wordmark inverts.
   *
   * No-op for `icon` (purple mark, reads on either ground) and for `wordmark`,
   * which already paints with `fill-current` and inherits the surrounding text
   * color — set `text-white` there instead.
   */
  tone?: 'dark' | 'light';
}

export function ButterLogo({
  variant = 'full',
  className = 'h-8 w-auto',
  title = 'Butter',
  ariaHidden = false,
  tone = 'dark'
}: ButterLogoProps) {
  // Dark-ground lockup is separate artwork, not a recolour — see the `tone` doc.
  if (variant === 'full' && tone === 'light') {
    return <ButterLogoFullLight className={className} title={title} ariaHidden={ariaHidden} />;
  }

  // Wordmark variant (text only)
  if (variant === 'wordmark') {
    return (
      <svg 
        className={className}
        viewBox="0 0 37 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={ariaHidden ? undefined : title}
        aria-hidden={ariaHidden}
      >
        <title>{title}</title>
        <path 
          className="fill-current"
          d="M3.64,2.87c-.7.01-1.84.39-2.16,1.06,0-.16,0-.36,0-.51,0-.98,0-2.49,0-3.08,0-.08,0-.16-.04-.22-.11-.14-.29-.11-.51-.12C.65,0,.24-.01.09.24,0,.39.02.64.01.83,0,2.5,0,4.18,0,5.85c-.03,1.36.43,2.49,1.27,3.21.76.66,1.84.97,3.14.79,1.59-.22,2.82-1.63,2.89-3.2.19-2.05-1.54-3.91-3.66-3.78h0ZM3.64,8.61h-.02c-1.64,0-2.65-1.7-2.01-3.14.69-1.59,3.03-1.74,3.91-.25.88,1.44-.12,3.4-1.88,3.39h0Z"
        />
        <path 
          className="fill-current"
          d="M14.24,3.72c0-.26.01-.46-.03-.57-.03-.09-.12-.13-.27-.15-.24-.02-.64-.02-.87,0-.27.01-.29.2-.29.42v2.67c0,.54.02.78-.08,1.21-.15.73-.8,1.29-1.58,1.27-.76.03-1.43-.53-1.59-1.25-.11-.43-.08-.67-.09-1.21v-2.69c0-.22-.02-.4-.29-.42-.12-.02-.29-.02-.45-.02-.24.02-.59-.05-.69.16-.04.11-.03.3-.03.57v2.31c0,.66-.02,1.24.18,1.87.37,1.26,1.63,2.05,2.95,2.02h.02c1.31.03,2.56-.76,2.93-2.02.2-.63.18-1.21.18-1.87v-2.31h0Z"
        />
        <path 
          className="fill-current"
          d="M18.64,8.44c-.82-.06-1.2-.64-1.16-1.4,0-.51,0-1.62,0-2.73.63,0,1.19,0,1.46,0,.16,0,.23-.07.24-.25.01-.13.01-.33.01-.52-.02-.3.06-.58-.32-.55h-1.39c0-.73,0-1.36-.01-1.67-.03-.13-.2-.13-.32-.14-.24,0-.63,0-.85,0-.13,0-.29.03-.29.18,0,.48,0,1.03,0,1.62h-.82c-.08,0-.16,0-.21.04-.04.03-.06.09-.07.18-.02.14-.01.37-.01.58,0,.19,0,.33.04.41h0c.03.07.08.09.15.1.25,0,.57,0,.93,0,0,1.02,0,2.04,0,2.86-.08,1.46.68,2.55,2.25,2.6h.02c.29.01.66.04.8-.15.16-.24.13-.59.06-.86-.07-.24-.29-.3-.51-.32h0Z"
        />
        <path 
          className="fill-current"
          d="M23.64,8.44c-.82-.06-1.2-.64-1.15-1.4,0-.51,0-1.62,0-2.73.63,0,1.19,0,1.46,0,.16,0,.23-.07.24-.25.01-.13.01-.33.01-.52-.02-.3.06-.58-.32-.55h-1.39c0-.73,0-1.36-.01-1.67-.03-.13-.2-.13-.32-.14-.24,0-.63,0-.85,0-.13,0-.29.03-.29.18,0,.48,0,1.03,0,1.62h-.82c-.08,0-.16,0-.21.04-.04.03-.06.09-.07.18-.02.14-.01.37-.01.58,0,.19,0,.33.04.41h0c.03.07.08.09.15.1.25,0,.57,0,.93,0,0,1.02,0,2.04,0,2.86-.08,1.46.68,2.55,2.25,2.6h.02c.29.01.66.04.8-.15.16-.24.13-.59.06-.86-.07-.24-.29-.3-.51-.32h0Z"
        />
        <path 
          className="fill-current"
          d="M30.17,3.61c-1.53-1.16-3.94-.84-5.05.73-1.68,2.33-.1,5.67,2.89,5.66h.02c1.15.02,2.26-.51,2.89-1.44.11-.18.41-.52.15-.67-.25-.1-.86-.1-1.19.04-.32.15-.57.5-.92.64-.42.2-.93.23-1.39.15-.9-.15-1.44-.9-1.58-1.74h5.2c.17.01.29-.08.32-.25.04-.21.04-.58,0-.9-.12-.87-.61-1.69-1.33-2.22ZM26.01,5.78c.14-.6.48-1.16,1.06-1.43.63-.3,1.46-.27,2.05.1.37.23.65.6.79,1.01.04.11.08.24.15.33-1.12,0-3.09,0-4.04,0h0Z"
        />
        <path 
          className="fill-current"
          d="M36.29,3.26c-.16-.35-.63-.23-.94-.2-.65.1-1.49.48-1.73,1.11.03-.12.05-.44.04-.61,0-.57-.52-.46-.94-.47-.18.01-.51-.03-.49.22,0,.89,0,5.45,0,6.36,0,.09,0,.16.11.19h0c.2.04.58.03.82.03.24-.01.48.02.51-.22.03-.61,0-1.95.01-2.8,0-.5-.02-.93.17-1.39.26-.65.91-1.07,1.61-1.12.26-.04.59.02.77-.19.16-.24.14-.67.04-.91Z"
        />
      </svg>
    );
  }

  if (variant === 'icon') {
    return (
      <svg 
        className={className}
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={ariaHidden ? undefined : title}
        aria-hidden={ariaHidden}
      >
        <title>{title}</title>
        {/* Purple gradient circles */}
        <path 
          className="fill-purple-600"
          d="M6.83,16.02c-.21-2.55.95-5.1,2.6-7.09,2.76-3.31,7.44-4.4,11.76-4.34,2.71.03,5.58.51,7.59,2.32l-.76-1.1C25.67,2.39,21.85.17,17.68.01c-.18,0-.35-.01-.53-.01-1.06,0-2.11.1-3.15.33-2.07.45-4.1,1.39-5.47,3-2.96,3.48-2.83,8.63-1.71,12.7h0Z"
        />
        <path 
          className="fill-purple-600"
          d="M16.11,24.54c-2.56.21-5.13-.49-7.13-2.14-3.33-2.74-4.42-7.4-4.36-11.7.03-2.69.52-5.55,2.33-7.55l-1.1.75C2.4,6.26.17,10.06.01,14.21c0,.18-.01.35-.01.52,0,1.05.1,2.1.33,3.13.45,2.06,1.4,4.07,3.02,5.44,3.5,2.94,9.48,2.15,12.77,1.24h0Z"
        />
        <path 
          className="fill-purple-600"
          d="M25.13,15.46c.21,2.55-.98,5.1-2.63,7.09-2.76,3.31-7.44,4.4-11.76,4.34-2.71-.03-5.58-.51-7.59-2.32l.76,1.1c2.36,3.42,6.18,5.64,10.35,5.8.18,0,.35.01.53.01,1.06,0,2.11-.1,3.15-.33,2.07-.45,4.1-1.39,5.47-3,2.96-3.48,2.85-8.63,1.73-12.7h0Z"
        />
        <path 
          className="fill-purple-600"
          d="M15.87,6.88c2.56-.21,5.13.49,7.13,2.14,3.33,2.74,4.42,7.4,4.36,11.7-.03,2.69-.52,5.55-2.33,7.55l1.1-.75c3.44-2.35,5.67-6.15,5.83-10.3,0-.18.01-.35.01-.52,0-1.05-.1-2.1-.33-3.13-.45-2.06-1.4-4.07-3.02-5.44-3.5-2.94-10.87-2.15-12.77-1.24h0Z"
        />
      </svg>
    );
  }

  return (
    <svg 
      className={className}
      viewBox="0 0 100 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaHidden ? undefined : title}
      aria-hidden={ariaHidden}
    >
      <title>{title}</title>
      
      {/* Text portion - dark gray */}
      <path 
        className="fill-gray-900"
        d="M44.96,11.67c-1.17.02-3.08.66-3.61,1.78,0-.27,0-.61,0-.85,0-1.63,0-4.18,0-5.16,0-.13,0-.27-.07-.37-.19-.24-.48-.18-.85-.21-.48,0-1.16-.02-1.42.41-.14.25-.13.66-.13.98-.01,2.8-.04,5.63-.02,8.43-.04,2.28.73,4.18,2.12,5.38,1.28,1.11,3.08,1.63,5.26,1.32,2.66-.37,4.72-2.74,4.85-5.36.31-3.44-2.58-6.55-6.14-6.34h0ZM44.96,21.28h-.03c-2.75.01-4.44-2.84-3.38-5.26,1.15-2.67,5.07-2.92,6.56-.42,1.47,2.42-.2,5.71-3.15,5.68h0Z"
      />
      <path 
        className="fill-gray-900"
        d="M62.72,13.09c0-.44.02-.77-.05-.95-.06-.15-.2-.22-.45-.25-.39-.04-1.08-.04-1.46,0-.45.03-.49.33-.48.7v4.48c0,.9.04,1.31-.13,2.03-.26,1.22-1.34,2.15-2.64,2.12-1.28.05-2.39-.89-2.66-2.1-.18-.72-.13-1.12-.14-2.03v-4.51c0-.37-.03-.67-.48-.7-.2-.03-.48-.03-.76-.03-.4.03-.99-.08-1.16.28-.07.18-.05.51-.05.95v3.88c0,1.11-.03,2.08.3,3.14.63,2.12,2.73,3.44,4.94,3.39h.03c2.2.05,4.29-1.28,4.92-3.39.34-1.06.31-2.03.3-3.14v-3.88h0Z"
      />
      <path 
        className="fill-gray-900"
        d="M70.1,21c-1.37-.09-2.02-1.08-1.94-2.34,0-.86,0-2.71,0-4.58,1.05,0,1.99,0,2.45,0,.27,0,.38-.12.4-.41.02-.22.02-.56.02-.87-.03-.51.1-.98-.53-.92h-2.33c0-1.23,0-2.28-.02-2.79-.05-.22-.34-.22-.54-.23-.4-.01-1.05-.02-1.42,0-.21,0-.49.05-.48.3,0,.8,0,1.73,0,2.71h-1.38c-.13,0-.26,0-.35.07-.06.05-.1.15-.12.3-.03.23-.02.62-.02.97,0,.31,0,.56.06.69h0c.04.12.14.16.25.17.42,0,.96,0,1.56,0,0,1.7,0,3.41,0,4.8-.13,2.44,1.13,4.27,3.78,4.35h.03c.49.02,1.11.06,1.34-.26.26-.4.21-1,.11-1.44-.11-.4-.48-.5-.86-.54h0Z"
      />
      <path 
        className="fill-gray-900"
        d="M78.49,21c-1.37-.09-2.02-1.08-1.94-2.34,0-.86,0-2.71,0-4.58,1.05,0,1.99,0,2.45,0,.27,0,.38-.12.4-.41.02-.22.02-.56.02-.87-.03-.51.1-.98-.53-.92h-2.33c0-1.23,0-2.28-.02-2.79-.05-.22-.34-.22-.54-.23-.4-.01-1.05-.02-1.42,0-.21,0-.49.05-.48.3,0,.8,0,1.73,0,2.71h-1.38c-.13,0-.26,0-.35.07-.06.05-.1.15-.12.3-.03.23-.02.62-.02.97,0,.31,0,.56.06.69h0c.04.12.14.16.25.17.42,0,.96,0,1.56,0,0,1.7,0,3.42,0,4.8-.13,2.44,1.13,4.27,3.78,4.35h.03c.49.02,1.11.06,1.34-.26.26-.4.21-1,.11-1.44-.11-.4-.48-.5-.86-.54h0Z"
      />
      <path 
        className="fill-gray-900"
        d="M89.42,12.91c-2.57-1.95-6.6-1.4-8.46,1.22-2.82,3.91-.17,9.5,4.84,9.49h.03c1.93.03,3.78-.86,4.85-2.42.18-.3.69-.88.26-1.12-.42-.17-1.45-.16-1.99.07-.53.25-.96.83-1.55,1.07-.7.33-1.57.39-2.33.25-1.51-.26-2.41-1.51-2.65-2.92h8.72c.29.02.49-.13.53-.41.07-.35.07-.97,0-1.5-.2-1.47-1.03-2.84-2.23-3.72ZM82.46,16.55c.23-1.01.8-1.95,1.78-2.4,1.05-.5,2.45-.45,3.43.16.62.38,1.09,1,1.32,1.69.07.19.13.4.25.55-1.88,0-5.18,0-6.77,0h0Z"
      />
      <path 
        className="fill-gray-900"
        d="M99.69,12.32c-.27-.59-1.05-.38-1.58-.33-1.08.17-2.5.8-2.9,1.86.05-.21.09-.74.06-1.02.01-.96-.86-.78-1.58-.79-.3.02-.86-.06-.82.36,0,1.5,0,9.13,0,10.67,0,.16,0,.28.19.33h.01c.34.07.97.05,1.38.05.39-.02.8.04.86-.37.04-1.03,0-3.26.02-4.69,0-.83-.04-1.56.29-2.33.43-1.09,1.53-1.79,2.7-1.87.44-.07,1,.03,1.29-.32.28-.41.23-1.12.07-1.53Z"
      />
      
      {/* Icon portion - purple */}
      <path 
        className="fill-purple-600"
        d="M6.83,16.02c-.21-2.55.95-5.1,2.6-7.09,2.76-3.31,7.44-4.4,11.76-4.34,2.71.03,5.58.51,7.59,2.32l-.76-1.1C25.67,2.39,21.85.17,17.68.01c-.18,0-.35-.01-.53-.01-1.06,0-2.11.1-3.15.33-2.07.45-4.1,1.39-5.47,3-2.96,3.48-2.83,8.63-1.71,12.7h0Z"
      />
      <path 
        className="fill-purple-600"
        d="M16.11,24.54c-2.56.21-5.13-.49-7.13-2.14-3.33-2.74-4.42-7.4-4.36-11.7.03-2.69.52-5.55,2.33-7.55l-1.1.75C2.4,6.26.17,10.06.01,14.21c0,.18-.01.35-.01.52,0,1.05.1,2.1.33,3.13.45,2.06,1.4,4.07,3.02,5.44,3.5,2.94,9.48,2.15,12.77,1.24h0Z"
      />
      <path 
        className="fill-purple-600"
        d="M25.13,15.46c.21,2.55-.98,5.1-2.63,7.09-2.76,3.31-7.44,4.4-11.76,4.34-2.71-.03-5.58-.51-7.59-2.32l.76,1.1c2.36,3.42,6.18,5.64,10.35,5.8.18,0,.35.01.53.01,1.06,0,2.11-.1,3.15-.33,2.07-.45,4.1-1.39,5.47-3,2.96-3.48,2.85-8.63,1.73-12.7h0Z"
      />
      <path 
        className="fill-purple-600"
        d="M15.87,6.88c2.56-.21,5.13.49,7.13,2.14,3.33,2.74,4.42,7.4,4.36,11.7-.03,2.69-.52,5.55-2.33,7.55l1.1-.75c3.44-2.35,5.67-6.15,5.83-10.3,0-.18.01-.35.01-.52,0-1.05-.1-2.1-.33-3.13-.45-2.06-1.4-4.07-3.02-5.44-3.5-2.94-10.87-2.15-12.77-1.24h0Z"
      />
    </svg>
  );
}
